import { useNavigate } from "@tanstack/react-router";
import { Copy01, Edit05, Eye, File06, Share03 } from "@untitledui/icons";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import {
  normalizeBriefContent,
  useDeleteSmartBrief,
  useDuplicateSmartBrief,
  useSmartBriefContent,
  useSmartBriefSharing,
  useUpdateSmartBriefSharing,
} from "@/api/smart-briefs";
import { StackedWithTeamAndLinkModal } from "@/components/application/modals/stacked-with-team-and-link-modal";
import { Button } from "@/components/base/buttons/button";
import { useMerchant } from "@/providers/MerchantProvider";
import { warning } from "@/providers/ToastProvider";
import { SmartBriefContent } from "./components/SmartBriefContent";
import { SmartBriefEditModal } from "./components/SmartBriefEditModal";

interface SmartBriefDisplayPageProps {
  briefId: string;
  productName?: string;
  onBack?: () => void;
}

export const SmartBriefDisplayPage = ({
  briefId,
  productName = "All Products",
  onBack,
}: SmartBriefDisplayPageProps) => {
  const navigate = useNavigate();
  const merchant = useMerchant();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Handle save completion - navigate to view mode (removes ?edit=true from URL)
  const handleSaveComplete = (savedBriefId: string) => {
    setShowEditModal(false);
    // Navigate to the same page but with edit=false to update the URL
    navigate({
      to: "/merchant/$merchantId/smart-briefs/brief/$briefId",
      params: { merchantId: merchant, briefId: savedBriefId },
      search: { edit: false },
      replace: true,
    });
  };

  const deleteBrief = useDeleteSmartBrief();
  const duplicateBrief = useDuplicateSmartBrief();
  const updateSharing = useUpdateSmartBriefSharing();

  const { data: briefData, isLoading, error } = useSmartBriefContent(briefId, productName);
  const { data: sharingData } = useSmartBriefSharing(briefId);

  const brief = briefData?.brief;
  // Normalize the content to convert snake_case keys (do_list) to camelCase (doList)
  const normalizedContent = briefData?.content
    ? normalizeBriefContent(briefData.content as unknown as Record<string, unknown>)
    : undefined;
  const examples = briefData?.examples || [];

  const handleDeleteBrief = async () => {
    try {
      await deleteBrief.mutateAsync(briefId);
      // Always navigate to the briefs list after deletion
      navigate({ to: "/merchant/$merchantId/smart-briefs", params: { merchantId: merchant }, replace: true });
    } catch (error) {
      console.error("Failed to delete brief:", error);
    }
  };

  const handleDuplicateBrief = async () => {
    try {
      const result = await duplicateBrief.mutateAsync(briefId);
      // Navigate to the duplicated brief
      if (result?.id) {
        navigate({
          to: "/merchant/$merchantId/smart-briefs/brief/$briefId",
          params: { merchantId: merchant, briefId: result.id },
        });
      }
    } catch (error) {
      console.error("Failed to duplicate brief:", error);
    }
  };

  const handleShare = async (isPublic: boolean, isPasswordProtected?: boolean, password?: string) => {
    try {
      await updateSharing.mutateAsync({
        briefId,
        isPublic,
        isPasswordProtected,
        password,
      });
    } catch (error) {
      console.error("Failed to update sharing:", error);
    }
  };

  // Preview opens the share URL in a new tab (only if already public)
  const handlePreview = () => {
    // If share URL exists and brief is public, open it
    if (sharingData?.isPublic && sharingData?.shareUrl) {
      window.open(`${window.location.origin}${sharingData.shareUrl}`, "_blank");
      return;
    }

    // Brief is not public - show warning and open share modal
    warning("This brief is private. Enable public link to preview.");
    setShowShareModal(true);
  };

  // Show loading state only when actively loading (not when there's an error)
  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading brief...</div>
        </div>
        {/* Render edit modal during loading - modal has its own loading state */}
        {showEditModal && (
          <SmartBriefEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            briefId={briefId}
            productName={productName}
          />
        )}
      </>
    );
  }

  // Show error only if there's an actual error or missing brief
  if (error || !brief) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <File06 className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Brief not found</h3>
            <p className="text-sm text-gray-500 mb-4">
              The brief you're looking for doesn't exist or has been deleted.
            </p>
            {onBack && (
              <Button color="secondary" size="md" onClick={onBack} iconLeading={<ArrowLeft className="w-4 h-4" />}>
                Back to Briefs
              </Button>
            )}
          </div>
        </div>
        {/* Render edit modal during error - modal has its own data fetching */}
        {showEditModal && (
          <SmartBriefEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            briefId={briefId}
            productName={productName}
          />
        )}
      </>
    );
  }

  const backButton = onBack && (
    <Button color="secondary" size="md" onClick={onBack} iconLeading={<ArrowLeft className="h-5 w-5" />}>
      See all briefs
    </Button>
  );

  const headerActions = (
    <>
      <Button
        color="secondary"
        size="md"
        onClick={handleDuplicateBrief}
        iconLeading={<Copy01 className="h-5 w-5 text-quaternary" />}
      >
        Duplicate
      </Button>
      <Button
        color="secondary"
        size="md"
        onClick={() => setShowShareModal(true)}
        iconLeading={<Share03 className="h-5 w-5 text-quaternary" />}
      >
        Share
      </Button>
      <Button
        color="secondary"
        size="md"
        onClick={handlePreview}
        iconLeading={<Eye className="h-5 w-5 text-quaternary" />}
      >
        Preview
      </Button>
      <Button
        color="secondary"
        size="md"
        onClick={() => setShowEditModal(true)}
        iconLeading={<Edit05 className="h-5 w-5 text-quaternary" />}
      >
        Edit
      </Button>
    </>
  );

  return (
    <>
      <SmartBriefContent
        brief={brief}
        content={normalizedContent as any}
        examples={examples}
        showCompanyLogo={true}
        backButton={backButton}
        headerActions={headerActions}
        bannerColor={brief.bannerColor ?? undefined}
      />

      {/* Sharing Modal - only render when open */}
      {showShareModal && (
        <StackedWithTeamAndLinkModal
          isOpen={showShareModal}
          onOpenChange={setShowShareModal}
          sharingData={sharingData}
          onUpdateSharing={handleShare}
          briefTitle={brief.title}
        />
      )}

      {/* Edit Modal - only render when open */}
      {showEditModal && (
        <SmartBriefEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          briefId={briefId}
          productName={productName}
        />
      )}
    </>
  );
};
